import { computed, inject, Injectable, Signal } from '@angular/core';

import { Action, Store } from '@ngrx/store';

import { createProviderToken } from '@core/common-legacy';
import type { Attribute, AttributeListData } from '@core/http-types';
import { PaginationState } from '@core/ngrx-legacy';
import { AttrDataScope } from '@core/tags-and-sources-data';
import { Tag } from '@core/tags-and-sources-types';

interface PaginationParams {
  query: string;
  page: number;
  limit: number;
}

interface TagsSourcesSelectContainerServiceContext {
  tagsSelector: (params: { scope: string }) => (arg: any) => { meta: PaginationState; rows: Tag[] };
  actionFactory: (payload: {
    payload: { params: PaginationParams; scope: string; add: boolean };
  }) => Action;
}

export const {
  provide: provideTagsSourcesSelectContainerServiceContext,
  inject: injectTagsSourcesSelectContainerServiceContext,
} = createProviderToken<TagsSourcesSelectContainerServiceContext>(
  'TagsSourcesSelectContainerServiceContext',
)();

@Injectable()
export class TagsSourcesSelectContainerService {
  private readonly store = inject(Store);

  public readonly initialParams = {
    query: '',
    page: 1,
    limit: 100,
  };
  public context = injectTagsSourcesSelectContainerServiceContext();
  public data = this.store.selectSignal(this.context.tagsSelector({ scope: AttrDataScope.Widget }));
  public collection = this.resolveCollections();
  public suggestions = this.resolveSuggestions();

  public fetchPaginatedCollection(params: PaginationParams, add = false) {
    this.store.dispatch(
      this.context.actionFactory({
        payload: { params: params, scope: AttrDataScope.Widget, add },
      }),
    );
  }

  private resolveCollections(): Signal<Array<{ id: number; value: string; count: number }>> {
    return computed(() =>
      this.data()?.rows.map(source => ({
        id: source.id,
        value: source.name,
        count: source.taggingsCount,
      })),
    );
  }

  private resolveSuggestions(): Signal<AttributeListData<Attribute>> {
    return computed((): AttributeListData => {
      const rows = this.collection();
      const meta = this.data().meta;
      const params = this.initialParams;

      return {
        isLoading: false,
        meta: {
          page: meta?.page || params?.page,
          limit: meta?.limit || params?.limit,
          totalCount: meta?.totalCount || 0,
        },
        rows: rows ? rows : [],
      };
    });
  }
}
